/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Modal to change profile question
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.3.1
 */

import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../../../assets';
import { Input, Select } from '../../../../../components/common';
import { DropDownFilterButtonSize } from '../../../../../components/common/DropDownFilter/DropDownFilter';
import { EInputKind } from '../../../../../components/common/Input/Input';
import { IOptionItemProps } from '../../../../../components/common/Select/Select';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSettings,
} from '../../../../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../../../../modules/internationalization/types/common';
import { IPageSettingsTranslation } from '../../../../../modules/internationalization/types/pages/pageSettings';
import { Loader } from '../../../../../modules/loader/components/Loader';
import { CrossButtonStyled } from '../../../../../modules/modal/components/styled/ModalStyled';
import ButtonServiceUserDenied from '../../../../../modules/role/components/ButtonServiceUserDenied';
import { IRootReducer } from '../../../../../reducers/rootReducer';
import { settingsProfileSaveQuestionAction, settingsProfileSetParamQuestionAction } from '../settingsProfileActions';
import { ESettingsProfileValidationCodes } from '../settingsProfileConstants';
import {
    makeSelectSettingsProfileIsLoadingModal,
    makeSelectSettingsProfileMetaQuestion,
    makeSelectSettingsProfileParamsQuestion,
} from '../settingsProfileSelectors';
import {
    ISettingsProfileMetaQuestion,
    ISettingsProfileParamsQuestion,
    TSettingsProfileSetParamQuestionActionPayload,
} from '../settingsProfileTypes';
import {
    SettingsProfileLabelStyled,
    SettingsProfilePopupContainerStyled,
    SettingsProfilePopupContentStyled,
    SettingsProfilePopupControlStyled,
    SettingsProfilePopupTitleStyled,
    SettingsProfileQuestionCurrentContainerStyled,
    SettingsProfileQuestionCurrentQuestionStyled,
    SettingsProfileQuestionSelectStyled,
} from '../styled/SettingsProfileStyled';

interface ISettingsProfileQuestionModalProps {
    onClose: () => void;
}

export type TSettingsProfileQuestionModalProps = ISettingsProfileQuestionModalProps & IConnectedProps & IDispatchProps;

export const SettingsProfileQuestionModal = (props: TSettingsProfileQuestionModalProps) => {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const { question: translationQuestion } = props.settingsTranslation.profile;

    const handleAnswerChange = (event: ChangeEvent<HTMLInputElement>): void => {
        let payload: TSettingsProfileSetParamQuestionActionPayload = { answer: event.target.value };
        if (props.params.answerErrorCode &&
            event.target.value.length >= props.params.selectedQuestion.answerMinLength) {
            payload = { ...payload, answerErrorCode: '' };
        }
        props.setParam(payload)
    };

    const handleAnswerBlur = (): void => {
        if (props.params.answer && props.params.answer.length < props.params.selectedQuestion.answerMinLength) {
            props.setParam({ answerErrorCode: ESettingsProfileValidationCodes.ANSWER_MIN_LENGTH })
        }
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        props.saveQuestion();
        setIsSubmitted(true);
    };

    const handleSetQuestion = (option: IOptionItemProps) => {
        const answerMinLength = props.meta.questionList
                .find(question => question.questionId === option.value)!.answerMinLength;
        const answerErrorCode = props.params.answer && props.params.answer.length < answerMinLength ?
            ESettingsProfileValidationCodes.ANSWER_MIN_LENGTH : '';
        props.setParam({
            selectedQuestion: {
                questionText: option.label,
                questionId: option.value as number,
                answerMinLength,
            },
            answerErrorCode,
        })
    };

    useEffect(() => {
        if (isSubmitted && !props.isLoadingModal) {
            props.onClose();
        }
    }, [isSubmitted, props.isLoadingModal])

    const getSelectOptions: IOptionItemProps[] = useMemo(() => {
        return props.meta.questionList.map(question => ({
            value: question.questionId,
            label: question.questionText,
        }))
    }, [props.meta.questionList])

    return (
            <SettingsProfilePopupContainerStyled data-component="profileQuestionModal" onSubmit={handleSubmit}>
                <CrossButtonStyled
                    alt="Close"
                    src={icons.cancelIcon}
                    onClick={() => props.onClose()}
                />

                <SettingsProfilePopupTitleStyled>
                    {translationQuestion.modalTitle}
                </SettingsProfilePopupTitleStyled>

                <SettingsProfilePopupContentStyled>
                    <SettingsProfileQuestionCurrentContainerStyled>
                        <SettingsProfileLabelStyled>
                            {translationQuestion.labelCurrent}
                        </SettingsProfileLabelStyled>
                        <SettingsProfileQuestionCurrentQuestionStyled data-name="currentQuestionModal">
                            {props.meta.currentQuestion}
                        </SettingsProfileQuestionCurrentQuestionStyled>
                    </SettingsProfileQuestionCurrentContainerStyled>
                    <SettingsProfileQuestionSelectStyled>
                        <SettingsProfileLabelStyled>
                            {translationQuestion.labelNew}
                        </SettingsProfileLabelStyled>
                        <Select
                            dataName="availableQuestionsSelect"
                            filterButtonSize={DropDownFilterButtonSize.medium}
                            kind='normal'
                            options={getSelectOptions}
                            placeholder={translationQuestion.placeholderNew}
                            selectedOption={{
                                label: props.params.selectedQuestion.questionText,
                                value: props.params.selectedQuestion.questionId,
                            }}
                            translation={props.commonTranslation}
                            onSelect={handleSetQuestion}
                        />
                    </SettingsProfileQuestionSelectStyled>
                    <SettingsProfileLabelStyled>
                        {translationQuestion.labelAnswer}
                    </SettingsProfileLabelStyled>
                    <Input
                        dataName="questionAnswerInput"
                        disabled={!props.params.selectedQuestion.questionText}
                        hintMessage={props.params.answerErrorCode ?
                            props.settingsTranslation.profile.errorCodes[props.params.answerErrorCode] : undefined}
                        kind={props.params.answerErrorCode ? EInputKind.error : undefined}
                        placeholder={translationQuestion.placeholderAnswer}
                        value={props.params.answer}
                        onBlur={handleAnswerBlur}
                        onChange={handleAnswerChange}
                    />
                </SettingsProfilePopupContentStyled>

                <SettingsProfilePopupControlStyled>
                    <ButtonServiceUserDenied
                        data-name="saveButton"
                        disabled={!props.params.answer || !!props.params.answerErrorCode}
                        kind="mediumNormal"
                        type="submit"
                    >
                        {props.settingsTranslation.profile.submit}
                    </ButtonServiceUserDenied>
                </SettingsProfilePopupControlStyled>

                <Loader showLoader={props.isLoadingModal}/>
            </SettingsProfilePopupContainerStyled>
    )
}

export interface IConnectedProps {
    commonTranslation: ICommonTranslation;
    isLoadingModal: boolean;
    meta: ISettingsProfileMetaQuestion;
    settingsTranslation: IPageSettingsTranslation;
    params: ISettingsProfileParamsQuestion;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
    isLoadingModal: makeSelectSettingsProfileIsLoadingModal(),
    meta: makeSelectSettingsProfileMetaQuestion(),
    params: makeSelectSettingsProfileParamsQuestion(),
    settingsTranslation: makeSelectTranslationPageSettings(),
});

export interface IDispatchProps {
    saveQuestion: () => void;
    setParam: (payload: TSettingsProfileSetParamQuestionActionPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    saveQuestion: () => dispatch(settingsProfileSaveQuestionAction()),
    setParam: payload => dispatch(settingsProfileSetParamQuestionAction(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(SettingsProfileQuestionModal);
